import React, {useCallback, useEffect, useState} from 'react';

// Black screen shown to players who haven't pressed Join Game once
// the show has started. JFD checks user.ready + scene index and renders
// this in place of the actual scene. Once the player presses Ready (or
// the operator marks them), the WS update broadcast refetches the user
// and this gate lifts.
export const NotReadyGate = () => (
    <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#000',
        color: 'rgba(255,255,255,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        fontStyle: 'italic',
        textAlign: 'center',
        padding: 24,
    }}>You're not in this game.</div>
);

// Players have to be Ready before this scene index for them to see any
// post-show-start scene. Otherwise they get the NotReadyGate.

export const Ready = ({isControl, receiveEvents}) => {
    const [players, setPlayers] = useState([]);

    const fetchPlayers = useCallback(() => {
        fetch('/users/', {credentials: 'include'})
            .then(r => r.json())
            .then(setPlayers)
            .catch(() => {});
    }, []);

    useEffect(() => receiveEvents && receiveEvents((d) => { if (d && d.update) fetchPlayers(); }), [fetchPlayers, receiveEvents]);
    useEffect(() => { fetchPlayers(); }, [fetchPlayers]);

    if (isControl) return <ReadyControl players={players}/>;
    return <ReadyPlayer onChanged={fetchPlayers}/>;
};

const ReadyControl = ({players}) => {
    const ready = players.filter(p => p.ready).length;
    const total = players.length;

    return (
        <div style={{
            width: '1500px',
            height: '900px',
            alignSelf: 'flex-start',
            display: 'flex',
            flexDirection: 'column',
            color: 'white',
            padding: 12,
            boxSizing: 'border-box',
        }}>
            <div style={{textAlign: 'center', fontSize: 64, fontWeight: 'bold', marginBottom: 12}}>
                {ready} / {total}
            </div>
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: 16,
                flex: 1,
                alignContent: 'flex-start',
            }}>
                {players.map(p => (
                    <div key={p.token} style={{
                        textAlign: 'center',
                        opacity: p.ready ? 1 : 0.4,
                    }}>
                        {p.character?.image_url ? (
                            <img src={p.character.image_url} alt="" style={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                objectFit: 'contain',
                                display: 'block',
                            }}/>
                        ) : (
                            <div style={{
                                width: '100%',
                                aspectRatio: '1 / 1',
                                backgroundColor: '#222',
                            }}/>
                        )}
                        <div style={{fontSize: 26, fontWeight: 'bold', lineHeight: 1.1, marginTop: 4}}>
                            {p.name}{p.ready && <span style={{color: '#36ff36'}}> ✓</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const STROKE_FILTER = 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)';

const ReadyPlayer = ({onChanged}) => {
    const [me, setMe] = useState(null);

    const fetchMe = useCallback(() => {
        fetch('/users/current', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(setMe)
            .catch(() => {});
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const markReady = () => {
        if (!me || me.ready) return;
        // Optimistic: flip ready immediately, roll back on failure.
        const prev = me;
        setMe({...me, ready: true});
        fetch('/users/current/ready', {
            method: 'POST',
            credentials: 'include',
        })
            .then((r) => {
                if (!r.ok) throw new Error('ready failed');
                fetchMe();
                onChanged && onChanged();
            })
            .catch(() => setMe(prev));
    };

    if (!me) return null;

    return (
        <div style={{
            position: 'relative',
            minHeight: '100dvh',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'calc(32px + env(safe-area-inset-top)) 24px calc(32px + env(safe-area-inset-bottom))',
            boxSizing: 'border-box',
            gap: 24,
        }}>
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                backgroundImage: 'url(/text-bg.webp)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                zIndex: -1,
                pointerEvents: 'none',
            }}/>
            <div style={{
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: 5,
                textTransform: 'uppercase',
                opacity: 0.65,
                textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
            }}>Playing As</div>

            {me.character?.image_url && (
                <img
                    src={me.character.image_url}
                    alt=""
                    style={{
                        width: 200,
                        height: 200,
                        objectFit: 'contain',
                        willChange: 'filter',
                        filter: STROKE_FILTER,
                    }}
                />
            )}

            <div style={{textAlign: 'center'}}>
                <div style={{
                    fontSize: 56,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    lineHeight: 1.05,
                    color: '#f1d28b',
                    textShadow: '0 0 18px rgba(232,177,90,0.7), 2px 2px 0 rgba(0,0,0,0.95)',
                    wordBreak: 'break-word',
                }}>{me.name}</div>
                {me.character?.nickname && (
                    <div style={{
                        fontSize: 22,
                        fontStyle: 'italic',
                        opacity: 0.85,
                        marginTop: 4,
                        textShadow: '1px 1px 0 rgba(0,0,0,0.9)',
                        wordBreak: 'break-word',
                    }}>{me.character.nickname}</div>
                )}
            </div>

            <button
                onClick={markReady}
                disabled={me.ready}
                className={!me.ready ? 'jfd-join-game-pulse' : undefined}
                style={{
                    width: '100%',
                    maxWidth: 360,
                    padding: '28px 24px',
                    fontSize: 36,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    letterSpacing: 3,
                    textTransform: 'uppercase',
                    color: me.ready ? '#0a3a0a' : '#062406',
                    backgroundColor: me.ready ? '#9be59b' : '#36ff36',
                    border: 'none',
                    borderRadius: 14,
                    boxShadow: me.ready
                        ? 'inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 0 rgba(0,0,0,0.35), 0 0 18px rgba(155,229,155,0.45)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -3px 0 rgba(0,0,0,0.45), 0 0 28px rgba(54,255,54,0.6), 4px 4px 0 rgba(0,0,0,0.5)',
                    cursor: me.ready ? 'default' : 'pointer',
                    transition: 'transform 100ms ease, box-shadow 100ms ease',
                    fontFamily: 'inherit',
                    textShadow: '1px 1px 0 rgba(255,255,255,0.25)',
                }}
            >
                {me.ready ? "You're In ✓" : "Join Game"}
            </button>
        </div>
    );
};
